import { useState, useCallback, useMemo } from 'react';
import * as yup from 'yup';
import type {
  FormData,
  UseShareLinkModalFormReturn,
  ValidationErrors,
  OnChangeEvent,
} from '@components/ShareLinkModal/ShareLinkModal.types';
import { useTranslation } from 'react-i18next';
import {
  FORM_DEFAULT_TOUCHED,
  FORM_DEFAULT_VALUES,
} from '@components/ShareLinkModal/_constants/ShareLinkModal.constants';
import { createValidationSchema } from '@components/ShareLinkModal/_helpers/create-validation-schema';

export const useShareLinkModalForm = (): UseShareLinkModalFormReturn => {
  const [formData, setFormData] = useState<FormData>(FORM_DEFAULT_VALUES);
  const [touched, setTouched] = useState(FORM_DEFAULT_TOUCHED);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { t } = useTranslation();
  const validationSchema = createValidationSchema(t);

  const validateField = useCallback(
    (name: keyof FormData, value: string): string | undefined => {
      try {
        validationSchema.validateSyncAt(name, { [name]: value });
        return undefined;
      } catch (error) {
        if (error instanceof yup.ValidationError) {
          return error.message;
        }
        return undefined;
      }
    },
    [validationSchema],
  );

  const validationErrors = useMemo<ValidationErrors>(() => {
    const errors: ValidationErrors = {};

    (Object.keys(formData) as Array<keyof FormData>).forEach(field => {
      if (touched[field]) {
        const error = validateField(field, formData[field]);
        if (error) {
          errors[field] = error;
        }
      }
    });

    return errors;
  }, [formData, validateField, touched]);

  const isFormValid = useMemo(() => {
    try {
      validationSchema.validateSync(formData);
      return true;
    } catch {
      return false;
    }
  }, [formData, validationSchema]);

  const onChange = useCallback((event: OnChangeEvent) => {
    const { name, value } = event;

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    setTouched(prev => ({
      ...prev,
      [name]: true,
    }));
  }, []);

  return {
    formData,
    onChange,
    isSubmitting,
    isFormValid,
    validationErrors,
    setIsSubmitting,
    setFormData,
    setTouched,
  };
};
